import React from 'react';
import { useLocation } from 'react-router-dom';
import Seo, { SITE } from '../Seo/Seo';
import RelatedCaseStudies from './RelatedCaseStudies';
import { formatCaseStudyDate } from '../../content/case-studies';
import './CaseStudyPage.css';

const truncate = (text, maxLength = 155) => {
  if (!text) return undefined;
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
};

/** Case-study detail page, fully generated from a post object (see src/content/case-studies). */
const CaseStudyPage = ({ post }) => {
  const { pathname } = useLocation();
  const { slug, industry, title, date, author, image, summary, website, contact, sections } = post;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: truncate(summary, 300),
    datePublished: date,
    author: { '@type': 'Organization', name: author },
    publisher: { '@id': `${SITE.url}/#organization` },
    mainEntityOfPage: `${SITE.url}/case-studies/${slug}`,
    inLanguage: 'de',
  };

  return (
    <article className="case-study-page">
      <Seo
        title={`${title} | Nexite IT`}
        description={truncate(summary)}
        path={pathname}
        type="article"
        jsonLd={jsonLd}
      />

      <header className="case-study-header">
        <h1>{title}</h1>
        <div className="case-study-meta">
          <span className="case-study-avatar" aria-hidden="true">
            {author.charAt(0)}
          </span>
          <span>{author}</span>
          <span className="case-study-metadot" aria-hidden="true">·</span>
          <time dateTime={date}>{formatCaseStudyDate(date)}</time>
        </div>
      </header>

      <div className="case-study-hero">
        <img src={image} alt={industry} />
        <a
          href={website}
          target="_blank"
          rel="noopener noreferrer"
          className="case-study-hero-cta"
        >
          Zur Live-Demo
        </a>
      </div>

      <div className="case-study-layout">
        <div className="case-study-body">
          <p className="case-study-lead">{summary}</p>

          {sections.map((section) => (
            <section key={section.heading} className="case-study-section">
              <h2>{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>

        <aside className="case-study-sidebar">
          <div className="case-study-sidebar-card">
            <p className="case-study-sidebar-intro">
              <strong>Klicken Sie auf unsere Live-Demo und erleben Sie die Lösung in Aktion.</strong>
            </p>

            <a
              href={website}
              className="case-study-website-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Zur Live-Demo
            </a>

            <div className="case-study-contact">
              <img src={contact.image} alt={contact.name} className="case-study-contact-image" />
              <div className="case-study-contact-text">
                <h4 className="case-study-contact-name">{contact.name}</h4>
                <p className="case-study-contact-role">{contact.title}</p>
              </div>
            </div>
            <p className="case-study-contact-description">{contact.description}</p>
          </div>
        </aside>
      </div>

      <RelatedCaseStudies post={post} />
    </article>
  );
};

export default CaseStudyPage;
